'use client';

import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useTranslation } from 'react-i18next';

import { formatDiaLongo } from '@/app/relatorios/helpers/formatRelatorios';
import { Notice } from '@/components/surface/Notice';

/** A Carteira, que é onde se importa. */
const CARTEIRA_HREF = '/ops';

/** Sem o parâmetro gravado vale o padrão do servidor: uma semana. */
const DIAS_PADRAO = 7;

/** Milissegundos de um dia, para a idade em dias de calendário. */
const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Dias de calendário entre a última importação e hoje. Calendário e não dias úteis: o
 * que envelhece a carteira é o tempo passar, e uma carteira de sexta lida na segunda
 * tem três dias de idade para quem a lê.
 */
function diasDesde(iso: string, hoje: Date): number | null {
  const quando = Date.parse(iso);
  if (Number.isNaN(quando)) {
    return null;
  }
  return Math.max(0, Math.floor((hoje.getTime() - quando) / MS_POR_DIA));
}

interface HomeCarteiraVelhaProps {
  /** Quando a última importação foi gravada; `null` enquanto ninguém importou. */
  ultimaEm: string | null;
  /** O limite em vigor; ausente vale o padrão. */
  avisarComMaisDeDias?: number;
  /** A leitura ainda não voltou, ou falhou: o aviso não dá notícia que não tem. */
  silenciar: boolean;
  className?: string;
}

/**
 * Avisa que a carteira está velha.
 *
 * Toda tela do produto lê a carteira do último arquivo importado, e nenhuma delas diz a
 * idade dela. Uma semana sem importar é uma semana de decisões tomadas sobre uma foto
 * velha com a mesma confiança de sempre: o número continua lá, igualzinho, e só quem
 * lembra da última importação sabe que ele envelheceu.
 */
export function HomeCarteiraVelha({ ultimaEm, avisarComMaisDeDias, silenciar, className }: HomeCarteiraVelhaProps) {
  const { t } = useTranslation('common');

  if (silenciar || ultimaEm === null || ultimaEm === '') {
    return null;
  }
  const dias = diasDesde(ultimaEm, new Date());
  const limite = avisarComMaisDeDias ?? DIAS_PADRAO;
  if (dias === null || dias <= limite) {
    return null;
  }

  return (
    <Notice
      tone="warning"
      icon="history"
      className={className}
      action={
        <LinkButton href={CARTEIRA_HREF} variant="text" tone="brand" size="sm" className="normal-case">
          {t('home.carteiraVelha.importar')}
          <MaterialSymbol icon="arrow_forward" size={16} />
        </LinkButton>
      }
    >
      <p>{t('home.carteiraVelha.aviso', { count: dias, quando: formatDiaLongo(ultimaEm.slice(0, 10)) })}</p>
    </Notice>
  );
}
